import { ToggleRequirementItem } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/components/ToggleRequirementItem.tsx';
import {
	attachKycFormToKycWorkflow,
	removeKycFormFromKycWorkflow,
} from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { MipFormAttachmentRes } from '@dealstack/shared/lib/generated/models/MipFormAttachmentRes';
import { DocumentWorkflowTemplateVisibleIn } from '@dealstack/shared/lib/generated/models/DocumentWorkflowTemplateVisibleIn';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useMipFormsWithAttachment } from '@dealstack/frontend/shared/hooks/useMipFormsWithAttachment';

interface Props {
	dealId: string;
	mipId: string;
	kycWorkflowId: string | undefined;
	audience: DocumentWorkflowTemplateVisibleIn;
}

export const KycFormsToggleSection = ({ dealId, mipId, kycWorkflowId, audience }: Props) => {
	const tMipManage = useTranslationMipManage();
	const queryClient = useQueryClient();
	const { data: forms } = useMipFormsWithAttachment(
		{ dealId, programmeId: mipId, kycWorkflowId },
		true
	);

	const [pendingIds, setPendingIds] = useState<string[]>([]);
	const formsQueryKey = [
		API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_KYC_FORMS_KYC_WORKFLOW_KYCWORKFLOWID_GET,
		kycWorkflowId,
	];

	const setPending = (templateId: string, on: boolean) =>
		setPendingIds((ids) => (on ? [...ids, templateId] : ids.filter((id) => id !== templateId)));

	const optimisticFlip = (templateId: string, isAttached: boolean) =>
		queryClient.setQueryData<ApiV1.Response<MipFormAttachmentRes[]>>(formsQueryKey, (old) =>
			old?.payload
				? {
						...old,
						payload: old.payload.map((form) =>
							form.templateId === templateId ? { ...form, isAttached } : form
						),
					}
				: old
		);

	const settle = (templateId: string) => {
		setPending(templateId, false);
		void queryClient.invalidateQueries({ queryKey: formsQueryKey });
		void queryClient.invalidateQueries({ queryKey: [API_PATHS.MIP_KYC_WORKFLOWS_GET] });
	};

	const onError = () =>
		showToastV2({ state: 'error', message: tMipManage('common.error.something_wrong') });

	const attachMutation = useMutation({ mutationFn: attachKycFormToKycWorkflow, onError });
	const removeMutation = useMutation({ mutationFn: removeKycFormFromKycWorkflow, onError });

	const audienceForms = (forms ?? []).filter((form) => form.visibleIn.includes(audience));
	if (audienceForms.length === 0) {
		return null;
	}

	const canToggle = !!kycWorkflowId && !!dealId;

	const onToggle = (form: (typeof audienceForms)[number]) => {
		if (!canToggle || !kycWorkflowId || pendingIds.includes(form.templateId)) {
			return;
		}
		if (form.isAttached && form.kycFormWorkflowStateId) {
			setPending(form.templateId, true);
			optimisticFlip(form.templateId, false);
			removeMutation.mutate(
				{ dealId, programmeId: mipId, kycFormWorkflowStateId: form.kycFormWorkflowStateId },
				{ onSettled: () => settle(form.templateId) }
			);
		} else if (!form.isAttached) {
			setPending(form.templateId, true);
			optimisticFlip(form.templateId, true);
			attachMutation.mutate(
				{ dealId, programmeId: mipId, kycWorkflowId, templateId: form.templateId },
				{ onSettled: () => settle(form.templateId) }
			);
		}
	};

	return (
		<>
			{audienceForms.map((form, index) => (
				<ToggleRequirementItem
					key={form.templateId}
					title={form.formName}
					description={form.instruction ?? ''}
					checked={form.isAttached}
					disabled={!canToggle || pendingIds.includes(form.templateId)}
					onToggle={() => onToggle(form)}
					showBorder={index < audienceForms.length - 1}
				/>
			))}
		</>
	);
};
